import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../config/supabase';
import './FeaturedAthletes.css';
import AthleteCard from './AthleteCard';

// Placeholder Card Component
const PlaceholderCard = () => {
  return (
    <div
      style={{
        position: 'relative',
        backgroundColor: '#000000',
        borderRadius: '20px',
        height: '400px',
        width: '271px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '2px dashed rgba(255, 209, 57, 0.3)'
      }}
    >
      <p style={{
        color: 'rgba(255, 209, 57, 0.6)',
        fontSize: '16px',
        fontWeight: '500',
        textAlign: 'center',
        padding: '0 20px',
        margin: '0'
      }}>
        Searching for the<br />Next Athlete
      </p>
    </div>
  );
};

const FeaturedAthletes = ({ setActiveScreen, setSelectedAthleteId }) => {
  const navigate = useNavigate();
  const { getFeaturedAthletes, loading } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [landingSettings, setLandingSettings] = useState(null);

  useEffect(() => {
    fetchFeaturedAthletes();
    fetchLandingSettings();
  }, []);

  const fetchLandingSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('landing_page_settings')
        .select('*')
        .eq('id', 1)
        .single();
      if (!error && data) {
        setLandingSettings(data);
      }
    } catch (err) {
      console.error('Error fetching landing settings:', err);
    }
  };

  const fetchFeaturedAthletes = async () => {
    const result = await getFeaturedAthletes(true); // Only visible ones
    if (result.success) {
      setAthletes(result.data || []);
    }
  };

  const handleAthleteClick = (spotlightId, orderPosition) => {
    setSelectedAthleteId(spotlightId, orderPosition);
    setActiveScreen('athlete-details');
  };

  // Calculate how many placeholder cards to show (max 8 total cards)
  const MIN_CARDS = 8;
  const placeholderCount = Math.max(0, MIN_CARDS - athletes.length);

  return (
    <div className="featured-athletes-wrapper">

      {/* Vector Background */}
      <div className="featured-athletes-vector-bg">
        <img src="/assets/images/vector-bg.png" alt="" />
      </div>

      <div className="container-fluid featured-athletes-container">
        <div className="row">
          <div className="col-12">
            {/* Header Section */}
            <div className="featured-athletes-header" style={{ visibility: landingSettings ? 'visible' : 'hidden' }}>
              <h3 className="featured-athletes-location">
                {landingSettings?.location_text}
              </h3>
              <h1 className="featured-athletes-title">{landingSettings?.heading_text}</h1>
              <p className="featured-athlete-subtitle">
                <span className="subtitle-gold-line subtitle-gold-line-left" />
                <span className="subtitle-text">{landingSettings?.subheading_text}</span>
                <span className="subtitle-gold-line subtitle-gold-line-right" />
              </p>
              <p className="featured-athlete-tagline">Recognizing exceptional youth athletes.</p>
              {/* <div className="featured-athletes-subtitle">
                <img
                  src="/assets/images/star-icon.png"
                  alt="Star"
                  className="featured-athletes-star-icon"
                />
                <span className="featured-athletes-subtitle-text">Proof lives here</span>
              </div> */}

              <div className="featured-athletes-divider">
                <div className="featured-athletes-gold-line"></div>
                <div className="featured-athletes-label">
                  <span className="featured-athletes-dots featured-athletes-dots-left">
                    <span className="dot dot-6"></span>
                    <span className="dot dot-5"></span>
                    <span className="dot dot-4"></span>
                    <span className="dot dot-3"></span>
                    <span className="dot dot-2"></span>
                    <span className="dot dot-1"></span>
                  </span>
                  <span className="featured-athletes-label-text">Featured Athletes</span>
                  <span className="featured-athletes-dots featured-athletes-dots-right">
                    <span className="dot dot-1"></span>
                    <span className="dot dot-2"></span>
                    <span className="dot dot-3"></span>
                    <span className="dot dot-4"></span>
                    <span className="dot dot-5"></span>
                    <span className="dot dot-6"></span>
                  </span>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#fff' }}>
                <p>Loading featured athletes...</p>
              </div>
            )}

            {/* Empty State */}
            {!loading && athletes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#fff' }}>
                <p>No featured athletes yet. Check back soon!</p>
              </div>
            )}

            {/* Athletes Grid */}
            <div className="row featured-athletes-grid">
              {/* Real Athletes */}
              {athletes.map((featured) => (
                <div
                  key={featured.id}
                  className="col-lg-3 col-md-6 col-sm-6 col-12 featured-athletes-col"
                >
                  <AthleteCard
                    image={featured.spotlight?.hero_image || '/assets/images/athlete-placeholder.png'}
                    name={featured.spotlight?.athlete_name}
                    sport={featured.spotlight?.primary_sport}
                    location={`${featured.spotlight?.city || ''}, ${featured.spotlight?.state || ''}`}
                    timeLabel={featured.spotlight?.time_label}
                    athleteNumber={featured.order_position}
                    spotlightLabel={featured.spotlight?.regional_label}
                    onBadgeClick={() => handleAthleteClick(featured.spotlight_id, featured.order_position)}
                  />
                </div>
              ))}

              {/* Placeholder Cards (only show if less than 8 athletes) */}
              {placeholderCount > 0 && Array.from({ length: placeholderCount }).map((_, index) => (
                <div
                  key={`placeholder-${index}`}
                  className="col-lg-3 col-md-6 col-sm-6 col-12 featured-athletes-col"
                >
                  <PlaceholderCard />
                </div>
              ))}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturedAthletes;
